import {
  ChatCreateRecordAction,
  ChatManageRelationshipAction,
  ChatProposal,
  ChatRecordUpdateAction,
  ChatSendEmailAction,
} from "@mapmycustomers/shared/types/chat/ChatContent";
import { FieldAgentAcceptedProposal } from "@mapmycustomers/shared/types/chat/FieldAgentChat";
import {
  canChooseRelatedRecord,
  supportsPrimaryFlag,
} from "@mapmycustomers/shared/util/chat/manageRelationshipPayload";

import composeSendEmailBody, { EmailComposeText } from "@app/util/chat/composeSendEmailBody";
import { resolveEffectivePrimary } from "@app/util/chat/proposalRelationship";
import loggingService from "@app/util/logging";

import {
  CREATE_RECORD_ENTITY_TYPE_KEY,
  CREATE_RECORD_LAYOUT_ID_KEY,
  readFinalEntityType,
  toCreateRecordFinalValues,
} from "./createRecordDraft";
import { toCreateRouteAcceptedItem } from "./createRouteDraft";
import { toLogActivityFinalValues } from "./logActivityDraft";
import { toRecordUpdateFinalValues } from "./recordUpdateDraft";
import { toRouteStopPickAcceptedItem } from "./routeStopPickDraft";

export type { EmailComposeText };

// One serializer per action type, so a new proposal story adds its entry here and its row
// renderer in the registry — and never touches the apply saga. finalValues is sparse: only
// keys the rep actually changed ride the wire; equal-to-proposed applies as proposed.
const toLogNoteAcceptedItem = (
  proposal: ChatProposal,
  draft: Record<string, unknown> | undefined
): FieldAgentAcceptedProposal => {
  const note = typeof draft?.note === "string" ? draft.note : undefined;
  const body = proposal.action.type === "log_note" ? proposal.action.body : undefined;
  return note !== undefined && note !== body
    ? { finalValues: { note }, proposalId: proposal.proposalId }
    : { proposalId: proposal.proposalId };
};

// finalValues has no key allowlist on the platform: a misspelled key returns `applied` with the edit
// silently discarded, never a 400. So the keys are declared once, here, and built from this type.
type SendEmailFinalValues = {
  body?: string;
  sendAt?: string;
  subject?: string;
};

const toSendEmailAcceptedItem = (
  proposal: ChatProposal,
  action: ChatSendEmailAction,
  draft: Record<string, unknown> | undefined,
  { boilerplateText, signatureText }: EmailComposeText
): FieldAgentAcceptedProposal => {
  const finalValues: SendEmailFinalValues = {};

  const subject = typeof draft?.subject === "string" ? draft.subject : undefined;
  if (subject !== undefined && subject !== action.email.subject) {
    finalValues.subject = subject;
  }

  // Composed for BOTH submit paths, and by the card's preview, through one shared helper — an
  // untouched row must not ship without the signature and boilerplate that an edited row carries,
  // and the rep must read the same body that gets sent.
  const composedBody = composeSendEmailBody(action.email.body, draft?.body, {
    boilerplateText,
    signatureText,
  });
  if (composedBody !== action.email.body) {
    finalValues.body = composedBody;
  }

  // An empty string clears a proposed schedule; a missing key keeps it. Sending "" when nothing was
  // proposed would say "clear" about a schedule that never existed.
  const sendAt = typeof draft?.sendAt === "string" ? draft.sendAt : undefined;
  if (sendAt !== undefined && sendAt !== (action.email.sendAt ?? "")) {
    finalValues.sendAt = sendAt;
  }

  // recipientIds is deliberately never sent: the platform resolves an edited list against Contacts
  // and forces the type, so this release keeps the proposed recipients read-only.
  return Object.keys(finalValues).length
    ? { finalValues, proposalId: proposal.proposalId }
    : { proposalId: proposal.proposalId };
};

// The editor only ever drafts a stage it resolved from the store, so "is this lost?" is decided
// there and carried in the draft: a serializer with no store access cannot re-derive it.
const toChangeDealStageAcceptedItem = (
  proposal: ChatProposal,
  draft: Record<string, unknown> | undefined
): FieldAgentAcceptedProposal => {
  if (proposal.action.type !== "change_deal_stage" || !draft) {
    return { proposalId: proposal.proposalId };
  }
  const { action } = proposal;
  const finalValues: Record<string, unknown> = {};
  const stageId = typeof draft.stageId === "number" ? draft.stageId : undefined;
  if (stageId !== undefined && stageId !== action.updatedStage.id) {
    finalValues.stageId = stageId;
  }
  // Loss values ride only when the rep actually changed them; BE applies the proposal's own
  // otherwise. Dropping them for a non-lost destination is belt-and-braces: BE gates on the
  // resolved destination stage's type and discards any loss values it was handed for an ordinary
  // stage (mmc-server change-deal-stage/handler.ts:169-178, verified).
  //
  // The one shape finalValues cannot express is a loss value the rep CLEARED while staying on a
  // lost stage. BE resolves it with `??` against the proposal's own value, so omitting the key
  // reapplies the agent's — and `null` is worse than omitting, since `??` treats them alike. Do not
  // invent a client-side encoding for it. Left unreported deliberately: reaching it needs a layout
  // that leaves dealLossReasonId optional on a lost stage, and the editor blocks Done whenever the
  // layout requires it, exactly as the Deal form does.
  const lost = draft.isLostStage === true;
  if (lost) {
    const reasonId =
      typeof draft.dealLossReasonId === "number" ? draft.dealLossReasonId : undefined;
    if (reasonId !== undefined && reasonId !== action.lossReason?.id) {
      finalValues.dealLossReasonId = reasonId;
    }
    const comment = typeof draft.dealLossComment === "string" ? draft.dealLossComment : undefined;
    if (comment !== undefined && comment !== action.lossComment) {
      finalValues.dealLossComment = comment;
    }
  }
  return Object.keys(finalValues).length > 0
    ? { finalValues, proposalId: proposal.proposalId }
    : { proposalId: proposal.proposalId };
};

// manage_relationship edits through finalRelatedEntity rather than finalValues: one record id, plus
// the primary flag on the one key that honors it. The platform re-resolves the id and re-checks its
// type, so nothing but the id needs to travel.
//
// `primary` is three-state on the wire. Omitting it keeps whatever the agent proposed; sending a
// boolean overrides it, `false` included — overrideSetPrimary returns any boolean and the strategy
// prefers it with `??`. So the rep can untick a primary the agent proposed, and this must send that.
const toManageRelationshipAcceptedItem = (
  proposal: ChatProposal,
  action: ChatManageRelationshipAction,
  draft: Record<string, unknown> | undefined
): FieldAgentAcceptedProposal => {
  // Every unlink applies as proposed: a Company-owned scalar clears the one link the Company holds,
  // and a collection unlink removes the record the agent named. Neither offers the rep a swap, so no
  // record travels. Enforced here as well as in the bodies, so a stray draft cannot reach the wire.
  if (!canChooseRelatedRecord(action)) {
    return { proposalId: proposal.proposalId };
  }
  const proposedId = action.relatedEntity?.id;
  const draftId = typeof draft?.relatedEntityId === "number" ? draft.relatedEntityId : undefined;
  const id = draftId ?? proposedId;
  // What reaches here is a link, and the inbound parser rejects a link that names no record — so
  // this is unreachable defence rather than a live branch. Kept because it is one line and the
  // alternative is putting `finalRelatedEntity: { id: undefined }` on the wire.
  if (id === undefined) {
    return { proposalId: proposal.proposalId };
  }
  const primarySupported = supportsPrimaryFlag(action);
  const proposedPrimary = action.setPrimary === true;
  const effectivePrimary = resolveEffectivePrimary(action, draft);
  const primaryChanged = primarySupported && effectivePrimary !== proposedPrimary;
  const idChanged = draftId !== undefined && draftId !== proposedId;
  return idChanged || primaryChanged
    ? {
        finalRelatedEntity: { id, ...(primaryChanged ? { primary: effectivePrimary } : {}) },
        proposalId: proposal.proposalId,
      }
    : { proposalId: proposal.proposalId };
};

// On log_activity, finalLayoutId is never sent: nothing on the apply path reads it.
// Every rule for this type lives in logActivityDraft, beside the draft it reads.
const toLogActivityAcceptedItem = (
  proposal: ChatProposal,
  draft: Record<string, unknown> | undefined
): FieldAgentAcceptedProposal => {
  const finalValues =
    proposal.action.type === "log_activity"
      ? toLogActivityFinalValues(proposal.action, draft)
      : undefined;
  return finalValues
    ? { finalValues, proposalId: proposal.proposalId }
    : { proposalId: proposal.proposalId };
};

// The two halves obey opposite rules, and this is the only place that is true.
//
// Standard fields are SPARSE and may never widen: the apply iterates the stored proposal's changes,
// so a key that is not among them is discarded while the outcome still reads `applied`. Sending one
// would be silent data loss with a green check on it.
//
// Custom fields are ALL OR NOTHING: finalValues.customFields is a whole-set replacement. [] wipes
// every proposed edit because it is not nullish and wins the platform's `??`; a partial array drops
// whatever it omits; and the key must be absent to accept the proposed set unchanged.
// Both of this type's rules live in recordUpdateDraft, beside the draft they read: standard keys
// sparse and never widened, custom fields a whole-set replacement.
const toRecordUpdateAcceptedItem = (
  proposal: ChatProposal,
  action: ChatRecordUpdateAction,
  draft: Record<string, unknown> | undefined
): FieldAgentAcceptedProposal => {
  const finalValues = toRecordUpdateFinalValues(action, draft);
  return finalValues
    ? { finalValues, proposalId: proposal.proposalId }
    : { proposalId: proposal.proposalId };
};

// finalEntityType rides only when the proposal arrived typeless; finalLayoutId is the parent
// layout's id, so a funnel/stage child layout would be refused.
const toCreateRecordAcceptedItem = (
  proposal: ChatProposal,
  action: ChatCreateRecordAction,
  draft: Record<string, unknown> | undefined
): FieldAgentAcceptedProposal => {
  const finalValues = toCreateRecordFinalValues(action, draft);
  const isTypeless = action.targetEntityType === null;
  const finalEntityType = isTypeless ? readFinalEntityType(draft) : undefined;
  return {
    ...(finalValues ? { finalValues } : {}),
    ...(finalEntityType ? { [CREATE_RECORD_ENTITY_TYPE_KEY]: finalEntityType } : {}),
    ...(typeof draft?.[CREATE_RECORD_LAYOUT_ID_KEY] === "number"
      ? { [CREATE_RECORD_LAYOUT_ID_KEY]: draft[CREATE_RECORD_LAYOUT_ID_KEY] }
      : {}),
    proposalId: proposal.proposalId,
  };
};

export const toAcceptedItem = (
  proposal: ChatProposal,
  draft: Record<string, unknown> | undefined,
  emailText: EmailComposeText
): FieldAgentAcceptedProposal => {
  switch (proposal.action.type) {
    case "log_note":
      return toLogNoteAcceptedItem(proposal, draft);
    case "manage_relationship":
      return toManageRelationshipAcceptedItem(proposal, proposal.action, draft);
    // Accept-or-reject: the apply envelope has no route- or position-shaped member and the
    // platform discards anything extra silently, so there is nothing to send but the id.
    case "manage_route_stops":
      return { proposalId: proposal.proposalId };
    // The rep's pick rides as the target, never as finalValues — a stop order included.
    case "manage_route_stops_pick":
      return toRouteStopPickAcceptedItem(proposal.proposalId, proposal.action, draft);
    case "change_deal_stage":
      return toChangeDealStageAcceptedItem(proposal, draft);
    case "log_activity":
      return toLogActivityAcceptedItem(proposal, draft);
    case "record_update":
      return toRecordUpdateAcceptedItem(proposal, proposal.action, draft);
    case "send_email":
      return toSendEmailAcceptedItem(proposal, proposal.action, draft, emailText);
    case "create_record":
      return toCreateRecordAcceptedItem(proposal, proposal.action, draft);
    case "create_route":
      return toCreateRouteAcceptedItem(proposal, proposal.action, draft);
    case "unknown":
      return { proposalId: proposal.proposalId };
    default: {
      const unhandled: never = proposal.action;
      loggingService.error("No accepted-item serializer for proposal action", {
        unhandled,
      });
      return { proposalId: proposal.proposalId };
    }
  }
};
